import React, { FC, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import 'firebase/storage';
import { ui, button } from 'util/ui';
import { ORG_DEFAULTS, OrgSettings as Org } from 'hooks/useOrgSettings';
import { storageBucketRef } from 'util/constants';
import { DEFAULT_MAIN_TAGS, MAX_MAIN_TAGS, cleanTags } from 'hooks/useMainTags';
import { COLOUR_DEFAULTS, COLOUR_LABELS, ThemeColours, applyColours, cleanColours, savedColours } from 'hooks/useThemeColours';
import { CrossIcon } from 'components/shell/Icons';
import { SettingsCard, SaveRow, fieldLabel, textInput } from './SettingsCard';

/**
 * Admin › Settings, top half (UI refresh, revised 5 Nov 2026):
 *   Organisation and logo · Help button · Colours · Main tags
 * Each box saves on its own. The logo saves as soon as it uploads.
 * Stored in the `settings` collection (admins write, everyone with a role reads, per the rules).
 */

const ORG_FIELDS: { key: keyof Org; label: string; placeholder: string }[] = [
  { key: 'companyName', label: 'Company name', placeholder: 'CoBox' },
  { key: 'databaseName', label: 'Database name', placeholder: 'Production Archive' },
];
const HELP_FIELDS: { key: keyof Org; label: string; placeholder: string }[] = [
  { key: 'helpName', label: 'Contact name', placeholder: 'e.g. Aaron Petersen' },
  { key: 'helpEmail', label: 'Email', placeholder: 'support@company.com' },
  { key: 'helpPhone', label: 'Phone', placeholder: '+61 …' },
  { key: 'helpHours', label: 'Hours', placeholder: 'Mon–Fri, 9am–5pm AEST' },
  { key: 'helpMessage', label: 'Message', placeholder: ORG_DEFAULTS.helpMessage },
];

const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 };

const pick = (o: Partial<Org>, keys: (keyof Org)[]) =>
  keys.reduce((acc, k) => ({ ...acc, [k]: String((o as any)[k] || '').trim() }), {} as Record<string, string>);

const settingsDoc = (id: string) => firebase.firestore().collection('settings').doc(id);

const Field: FC<{ src: Partial<Org>; set(f: (o: Partial<Org>) => Partial<Org>): void; f: { key: keyof Org; label: string; placeholder: string } }> = ({ src, set, f }) => (
  <label style={fieldLabel}>
    {f.label}
    <input
      style={textInput}
      value={(src as any)[f.key] || ''}
      placeholder={f.placeholder}
      onChange={(e) => {
        const v = e.target.value;
        set((o) => ({ ...o, [f.key]: v }));
      }}
    />
  </label>
);

/** Organisation names and the logo, top left of every page and in the footer. */
const Organisation: FC = () => {
  const [org, setOrg] = useState<Partial<Org>>({});
  const [msg, setMsg] = useState('');
  const [logoMsg, setLogoMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    settingsDoc('organisation')
      .get()
      .then((d) => setOrg((d.data() as Partial<Org>) || {}));
  }, []);

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await settingsDoc('organisation').set(pick(org, ['companyName', 'databaseName']), { merge: true });
      setMsg('Saved. Everyone sees the change straight away.');
    } catch (e) {
      setMsg(`Not saved: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  // The logo is saved the moment it is uploaded (Aaron, 5 Nov 2026: a separate Save made it look broken).
  const setLogo = async (logoURL: string, done: string) => {
    await settingsDoc('organisation').set({ logoURL }, { merge: true });
    setOrg((o) => ({ ...o, logoURL }));
    setLogoMsg(done);
  };

  const uploadLogo = async (file: File) => {
    if (!/^image\/(png|jpeg|svg\+xml|webp)$/.test(file.type)) return setLogoMsg('Use a PNG, JPG, SVG or WebP image.');
    if (file.size > 2 * 1024 * 1024) return setLogoMsg('Keep the logo under 2 MB.');
    setLogoBusy(true);
    setLogoMsg('Uploading logo…');
    try {
      const ref = firebase.storage().refFromURL(storageBucketRef.cobox_images).child(`branding/logo-${Date.now()}-${file.name}`);
      await ref.put(file, { contentType: file.type });
      await setLogo(await ref.getDownloadURL(), '✓ Logo saved. It now shows top left for everyone.');
    } catch (e) {
      setLogoMsg(`Logo not saved: ${(e as Error).message}`);
    } finally {
      setLogoBusy(false);
    }
  };

  const useInitial = async () => {
    setLogoBusy(true);
    try {
      await setLogo('', '✓ Saved. The first letter of the company name shows instead.');
    } catch (e) {
      setLogoMsg(`Not saved: ${(e as Error).message}`);
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <SettingsCard id="org-h" title="Organisation and logo" note="Shown top left on every page and on the left of the footer.">
      <div style={grid}>
        {ORG_FIELDS.map((f) => (
          <Field key={f.key} src={org} set={setOrg} f={f} />
        ))}
      </div>

      <div style={{ ...fieldLabel, marginTop: 18 }}>Logo</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
        <div
          style={{
            height: 56,
            minWidth: 56,
            padding: '0 8px',
            boxSizing: 'border-box',
            borderRadius: 12,
            border: `1px dashed ${ui.control}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: ui.field,
          }}
        >
          {org.logoURL ? (
            <img src={org.logoURL} alt="Current logo" style={{ maxHeight: 40, maxWidth: 160 }} />
          ) : (
            <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 10, background: ui.accent, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18 }}>
              {(org.companyName || ORG_DEFAULTS.companyName).charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <label className="archy-btn" style={{ ...button('plain'), height: 40, cursor: logoBusy ? 'wait' : 'pointer' }}>
          {org.logoURL ? 'Replace logo' : 'Upload logo'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            style={{ display: 'none' }}
            disabled={logoBusy}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              e.target.value = ''; // so choosing the same file again still works
              if (file) uploadLogo(file);
            }}
          />
        </label>
        {org.logoURL && (
          <button type="button" className="archy-btn" style={{ ...button('plain'), height: 40 }} onClick={useInitial} disabled={logoBusy}>
            Use the initial instead
          </button>
        )}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 12, color: ui.muted }}>
        PNG or SVG with a transparent background works best, about 36 px tall. Saved as soon as it uploads.
      </p>
      {logoMsg && (
        <p role="status" style={{ margin: '6px 0 0', fontSize: 13, color: logoMsg.startsWith('✓') ? '#0B6B3F' : ui.muted, fontWeight: 500 }}>
          {logoMsg}
        </p>
      )}

      <SaveRow onSave={save} busy={busy} message={msg} />
    </SettingsCard>
  );
};

/** What people see when they press Help, bottom right. */
const Help: FC = () => {
  const [help, setHelp] = useState<Partial<Org>>({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    settingsDoc('help')
      .get()
      .then((d) => setHelp((d.data() as Partial<Org>) || {}));
  }, []);
  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await settingsDoc('help').set(pick(help, HELP_FIELDS.map((f) => f.key)), { merge: true });
      setMsg('Saved. The Help pop-up shows it straight away.');
    } catch (e) {
      setMsg(`Not saved: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <SettingsCard id="help-h" title="Help button" note="What people see when they press Help, bottom right.">
      <div style={grid}>
        {HELP_FIELDS.map((f) => (
          <Field key={f.key} src={help} set={setHelp} f={f} />
        ))}
      </div>
      <SaveRow onSave={save} busy={busy} message={msg} />
    </SettingsCard>
  );
};

/** The customer's colours: project status and bucket bars. The only part of the look they can change. */
const Colours: FC = () => {
  const [colours, setColours] = useState<ThemeColours>(savedColours());
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    settingsDoc('colours')
      .get()
      .then((d) => setColours(cleanColours(d.data())))
      .catch(() => undefined);
    // Leaving without saving puts the saved colours back.
    return () => applyColours(savedColours());
  }, []);

  const change = (k: keyof ThemeColours, v: string) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(v.trim())) {
      setMsg('Use a 6-digit colour code, like #E5056A.');
      return;
    }
    const next = cleanColours({ ...colours, [k]: v.trim() });
    setColours(next);
    applyColours(next); // live preview across the app
    setMsg('Not saved yet. You are seeing a preview.');
  };

  const save = async () => {
    setBusy(true);
    try {
      await settingsDoc('colours').set(colours, { merge: true });
      setMsg('Saved. Everyone sees the new colours straight away.');
    } catch (e) {
      setMsg(`Not saved: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const isDefault = (Object.keys(COLOUR_DEFAULTS) as (keyof ThemeColours)[]).every((k) => colours[k] === COLOUR_DEFAULTS[k]);

  return (
    <SettingsCard id="colours-h" title="Colours" note="Your colours for projects and project buckets. Changes preview straight away; press Save to keep them.">
      <div style={grid}>
        {(Object.keys(COLOUR_DEFAULTS) as (keyof ThemeColours)[]).map((k) => (
          <label key={k} style={fieldLabel}>
            {COLOUR_LABELS[k].label}
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44, border: `1px solid ${ui.control}`, borderRadius: 10, padding: '0 6px 0 6px', boxSizing: 'border-box' }}>
              <input
                type="color"
                aria-label={`${COLOUR_LABELS[k].label} colour`}
                value={colours[k]}
                onChange={(e) => change(k, e.target.value)}
                style={{ width: 34, height: 30, padding: 0, border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer' }}
              />
              <input
                aria-label={`${COLOUR_LABELS[k].label} hex code`}
                defaultValue={colours[k]}
                key={colours[k]}
                maxLength={7}
                onBlur={(e) => change(k, e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                style={{ border: 'none', outline: 'none', font: 'inherit', fontSize: 14, fontWeight: 500, color: ui.ink, width: '100%', minWidth: 0, textTransform: 'uppercase' }}
              />
            </span>
            <span style={{ fontWeight: 400, color: ui.muted }}>{COLOUR_LABELS[k].help}</span>
          </label>
        ))}
      </div>

      {/* Mini preview of where each colour shows */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }} aria-hidden="true">
        {[true, false].map((done) => (
          <div key={String(done)} style={{ width: 150, borderRadius: 10, overflow: 'hidden', border: `2px solid ${done ? colours.complete : colours.incomplete}`, background: ui.surface }}>
            <div style={{ height: 18, background: done ? colours.complete : colours.incomplete }} />
            <div style={{ padding: '6px 8px', fontSize: 11, color: ui.muted }}>{done ? 'Complete project' : 'Incomplete project'}</div>
          </div>
        ))}
        <div style={{ width: 220, borderRadius: 10, overflow: 'hidden', border: `1px solid ${ui.line}`, background: ui.surface }}>
          <div style={{ height: 26, background: colours.bucket, color: '#FFFFFF', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', padding: '0 8px' }}>Videos & Audio</div>
          <div style={{ padding: '6px 8px', fontSize: 11, color: ui.muted }}>Bucket bar</div>
        </div>
      </div>

      <SaveRow onSave={save} busy={busy} message={msg}>
        {!isDefault && (
          <button
            type="button"
            className="archy-btn"
            style={{ ...button('plain'), height: 40 }}
            onClick={() => {
              setColours(COLOUR_DEFAULTS);
              applyColours(COLOUR_DEFAULTS);
              setMsg('Defaults restored in the preview. Press Save to keep them.');
            }}
          >
            Reset to defaults
          </button>
        )}
      </SaveRow>
    </SettingsCard>
  );
};

/** Admin › Settings › Main tags: the 8–10 categories the home menu filters by. */
const MainTags: FC = () => {
  const [tags, setTags] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    settingsDoc('tags')
      .get()
      .then((d) => {
        const saved = cleanTags(d.get('main'));
        setTags(saved.length ? saved : DEFAULT_MAIN_TAGS);
      })
      .catch(() => setTags(DEFAULT_MAIN_TAGS));
  }, []);
  const add = () => {
    const next = cleanTags([...tags, text]);
    if (next.length === tags.length && text.trim()) setMsg(tags.length >= MAX_MAIN_TAGS ? `Up to ${MAX_MAIN_TAGS} main tags.` : 'Already in the list.');
    else setMsg('');
    setTags(next);
    setText('');
  };
  const move = (i: number, d: number) =>
    setTags((t) => {
      const j = i + d;
      if (j < 0 || j >= t.length) return t;
      const c = t.slice();
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });
  const save = () =>
    settingsDoc('tags')
      .set({ main: cleanTags(tags) }, { merge: true })
      .then(() => setMsg('Saved. The menu on the Projects page updates straight away.'))
      .catch((e) => setMsg(`Not saved: ${(e as Error).message}`));
  const small: React.CSSProperties = { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: ui.muted, padding: '0 4px', display: 'inline-flex', alignItems: 'center' };
  return (
    <SettingsCard
      id="tags-h"
      title="Main tags"
      note="The categories people filter projects by on the Projects page, and the quick tags on each project. Keep it to about 8–10, in this order."
    >
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {tags.map((t, i) => (
          <li key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, height: 30, boxSizing: 'border-box', background: ui.subtle, borderRadius: 8, padding: '0 6px 0 12px', fontSize: 13 }}>
            {t}
            <button type="button" style={small} aria-label={`Move ${t} earlier`} onClick={() => move(i, -1)} disabled={i === 0}>
              ‹
            </button>
            <button type="button" style={small} aria-label={`Move ${t} later`} onClick={() => move(i, 1)} disabled={i === tags.length - 1}>
              ›
            </button>
            <button type="button" style={small} aria-label={`Remove ${t}`} title="Remove" onClick={() => setTags((x) => x.filter((y) => y !== t))}>
              <CrossIcon size={11} color={ui.muted} width={2.6} />
            </button>
          </li>
        ))}
      </ol>
      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={text}
          placeholder="Add a main tag"
          aria-label="Add a main tag"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          style={{ ...textInput, height: 40, width: 220 }}
        />
        <button type="button" className="archy-btn" style={{ ...button('plain'), height: 40 }} onClick={add} disabled={!text.trim()}>
          Add
        </button>
        <span style={{ fontSize: 12, color: ui.muted }}>
          {tags.length} of up to {MAX_MAIN_TAGS}
        </span>
      </div>
      <SaveRow onSave={save} message={msg} />
    </SettingsCard>
  );
};

const OrgSettings: FC = () => (
  <div style={{ fontFamily: ui.font, color: ui.ink }}>
    <Organisation />
    <Help />
    <Colours />
    <MainTags />
  </div>
);

export default OrgSettings;
